import { t } from "@lingui/macro";
import {
  type BudgetCategory,
  BudgetItemTypeEnum,
  type BudgetPosition,
  BulkConvertAmountDocument,
  type BulkConvertAmountQuery,
  type BulkConvertAmountQueryVariables,
  ExchangeRateSource,
} from "@src/__generated__/urql-graphql";
import type {
  TExpenseBudgetItem,
  TOurWorkBudgetItem,
} from "@src/components/modules/invoices/Create/models";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { appStore } from "@src/stores/AppStore";
import { EXCHANGE_RATE_DENOMINATION } from "@src/stores/forms/SettingsModalState/FinancialSettingsState";
import { can } from "@src/utils/components/permissions";
import { toApiDate } from "@src/utils/dates";
import { currency, denominate, nominate } from "@src/utils/formatters";
import {
  arrayLengthMin,
  gt,
  gte,
  lt,
  lte,
  onlyNumeric,
  required,
} from "@src/utils/forms/validators";
import { NumberModel } from "@src/utils/models";
import { differenceInCalendarDays, isAfter, isBefore, min } from "date-fns";
import { FieldState, FormState } from "formstate";
import { compact } from "lodash";
import { action, autorun, computed, makeObservable, observable } from "mobx";
import { InvoiceStepIds, type InvoiceStore, InvoiceTypeEnum } from "../store";
import type {
  InvoiceItemArgs,
  TExpenseBudgetSplitForm,
  TInvoicedPositionsForm,
  TInvoiceItemForm,
  TOurWorkBudgetSplitForm,
  TPrepaymentSplitForm,
} from ".";

export class Form {
  store: InvoiceStore;
  bulkConvertFetcher = new FetchHelper<
    BulkConvertAmountQuery,
    BulkConvertAmountQueryVariables
  >(BulkConvertAmountDocument);

  private requiredDueDateIf = (value: undefined | Date) => {
    if (this.store.isDueDateRequired) return required(value);
    return null;
  };

  private requiredIssueDateIf = (value: undefined | Date) => {
    if (this.store.isIssueDateRequired) return required(value);
    return null;
  };

  private requiredDateOfSupplyIf = (value: undefined | Date) => {
    if (this.store.type === InvoiceTypeEnum.Proforma) return null;
    return required(value);
  };

  private minDateOfSupply = (value: undefined | Date) => {
    if (this.store.type === InvoiceTypeEnum.Proforma) return null;
    if (!value) return null;

    const dates = this.store.selectedProjects.map(
      (i) => new Date(i.start_date),
    );
    if (!dates.length) return null;

    const minDate = min(dates);

    minDate.setHours(0, 0, 0, 0);
    value.setHours(0, 0, 0, 0);

    if (isBefore(value, minDate)) {
      return t`Cannot be sooner than the project has started`;
    }

    return null;
  };

  private dateOfSupplyAfterIssueDate = (value: Date | undefined) => {
    if (this.store.type === InvoiceTypeEnum.Proforma) return null;
    if (!value || !this.issue_date.$) return null;
    if (isAfter(value, this.issue_date.$)) {
      return t`Cannot be later than the issue date`;
    }
    return null;
  };

  private maxIssueDate = (value: undefined | Date) => {
    if (this.store.type === InvoiceTypeEnum.Proforma) return null;
    if (!value || !this.date_of_supply.value) return null;

    const maxDiffDays = 15;

    if (
      differenceInCalendarDays(value, this.date_of_supply.value) > maxDiffDays
    ) {
      return t`Max ${maxDiffDays} days from supply`;
    }

    return null;
  };

  private requiredIfCfoOrHigher = ($: string | undefined) => {
    if (can("requestForInvoicing_accessAccountingAttributes")) {
      return required($);
    }
    return null;
  };

  private requiredInvoiceOrderSequenceId = (value: undefined | string) => {
    return required(value) ? t`Please select invoice no first` : null;
  };

  private requiredIfCreditNote = (value: undefined | string) => {
    if (this.store.type === InvoiceTypeEnum.CreditNote) return required(value);
    return null;
  };

  client_id = new FieldState<string>("").validators(required);
  brand_ids = new FieldState<string[]>([]).validators(arrayLengthMin(1));
  billing_entity_id = new FieldState("").validators(required);
  contact_id = new FieldState("").validators();
  team_ids = new FieldState<string[]>([]).validators(arrayLengthMin(1));
  currency_id = new FieldState("").validators(required);
  project_ids = new FieldState<string[]>([]).validators((value) => {
    if (this.store.type === InvoiceTypeEnum.Proforma) return required(value[0]);
    return null;
  });
  owner_id = new FieldState<string>("");
  media_ids = new FieldState<string[]>([]);
  due_date = new FieldState<Date | undefined>(undefined)
    .validators(this.requiredDueDateIf)
    .enableAutoValidation();
  issue_date = new FieldState<Date | undefined>(undefined)
    .validators(this.requiredIssueDateIf, this.maxIssueDate)
    .enableAutoValidation();
  date_of_supply = new FieldState<Date | undefined>(undefined)
    .validators(
      this.requiredDateOfSupplyIf,
      this.minDateOfSupply,
      this.dateOfSupplyAfterIssueDate,
    )
    .enableAutoValidation();
  vat_percent_informative = new FieldState<string>("").validators(onlyNumeric);
  vat_reverse_charge = new FieldState(false);
  refresh_billing_entity = new FieldState(false);
  order_ids = new FieldState<string[]>([]);
  outgoing_proforma_invoice_ids = new FieldState<string[]>([]);
  payment_receipt_ids = new FieldState<string[]>([]);
  invoice_items = new FormState(observable.array<TInvoiceItemForm>([]));
  our_work_budget_splits = new FormState<
    Map<TOurWorkBudgetItem["id"], TOurWorkBudgetSplitForm>
  >(new Map());
  expense_budget_splits = new FormState<
    Map<TExpenseBudgetItem["id"], TExpenseBudgetSplitForm>
  >(new Map());
  prepayment_splits = new FormState(observable.array<TPrepaymentSplitForm>([]));
  note = new FieldState("");
  internal_note = new FieldState("");
  text_on_invoice = new FieldState("");
  exchange_rate: FieldState<NumberModel>;
  exchange_rate_source = new FieldState<ExchangeRateSource>(
    ExchangeRateSource.Client,
  );
  workspace_exchange_rate = new FieldState<NumberModel>(
    NumberModel.fromValue(1, EXCHANGE_RATE_DENOMINATION),
  );
  workspace_exchange_rate_source = new FieldState<ExchangeRateSource>(
    ExchangeRateSource.Budget,
  );
  total_in_words = new FieldState<string | undefined>(undefined);
  invoice_no = new FieldState<string | undefined>(undefined);
  invoice_order_sequence_id = new FieldState("").validators(
    this.requiredInvoiceOrderSequenceId,
  );
  workspace_company_id = new FieldState("").validators(required);
  workspace_bank_account_id = new FieldState<string | undefined>(
    undefined,
  ).validators(this.requiredIfCfoOrHigher);
  invoicePaymentMethodId = new FieldState<string | undefined>(undefined);
  credit_note_outgoing_invoice_id = new FieldState<string | undefined>(
    undefined,
  ).validators(this.requiredIfCreditNote);

  steps = {
    [InvoiceStepIds.ProjectDetails]: new FormState([
      this.team_ids,
      this.client_id,
      this.brand_ids,
      this.billing_entity_id,
      this.contact_id,
      this.project_ids,
      this.media_ids,
      this.owner_id,
      this.workspace_company_id,
      this.workspace_bank_account_id,
      this.credit_note_outgoing_invoice_id,
    ]),
    [InvoiceStepIds.BudgetItems]: new FormState([
      this.expense_budget_splits,
      this.our_work_budget_splits,
      this.prepayment_splits,
    ]),
    [InvoiceStepIds.InvoiceDetails]: new FormState<
      (FieldState<any> | FormState<any>)[]
    >([
      this.currency_id,
      this.invoice_items,
      this.due_date,
      this.issue_date,
      this.date_of_supply,
      this.order_ids,
      this.note,
      this.internal_note,
      this.text_on_invoice,
      this.total_in_words,
      this.vat_reverse_charge,
      this.invoice_no,
      this.invoice_order_sequence_id,
      this.invoicePaymentMethodId,
    ]),
  };

  form = new FormState(Object.values(this.steps));

  constructor(store: InvoiceStore) {
    makeObservable(this);
    this.store = store;
    this.exchange_rate = new FieldState(
      NumberModel.fromValue(
        1,
        this.store.appStore.workspaceStore.settings!.exchange_rate_denomination,
      ),
    ).validators(required, gt(0));
    this.steps[InvoiceStepIds.InvoiceDetails].$.push(this.exchange_rate);
    this.invoice_no.onChange(store.invoice_no);

    autorun(() => {
      const defaultBankAccountId =
        appStore.workspaceStore.getDefaultCompanyBankAccount(
          this.workspace_company_id.value,
        )?.id;

      if (!defaultBankAccountId) return;
      this.workspace_bank_account_id.onChange(defaultBankAccountId);
    });
  }

  formatGtLtMessage = (val: number) => {
    return currency.formatNominal(val, this.store.selectedCurrency.code, {
      fractionDigits: this.store.selectedCurrency.denomination,
    });
  };

  reset = () => {
    this.steps[InvoiceStepIds.ProjectDetails].reset();
    this.steps[InvoiceStepIds.BudgetItems].reset();
    this.vat_reverse_charge.reset();
    this.resetInvoiceItems();
    this.resetBudgetSplits();
    this.resetPrepaymentSplits();
  };

  resetInvoiceItems = () => {
    this.invoice_items.$.clear();
  };

  resetBudgetSplits = () => {
    this.expense_budget_splits.$.clear();
    this.our_work_budget_splits.$.clear();
  };

  resetPrepaymentSplits = () => {
    this.prepayment_splits.$.clear();
  };

  resetExchangeRate = () => {
    this.handleExchangeRateChange("1");
  };

  addInvoiceItem = (item: InvoiceItemArgs) => {
    this.invoice_items.$.push(this.createInvoiceItem(item));
  };

  addEmptyInvoiceItem() {
    this.addInvoiceItem({
      description: "",
      amount_in_currency: NumberModel.fromValue(
        0,
        this.store.selectedCurrency.denomination,
      ),
      unit_price: "",
      quantity: "",
      accounting_account_id: "",
      cost_center_id: undefined,
      tag_ids: [],
      vat_percent_informative: this.vat_percent_informative.$ ?? "0",
    });
  }

  removeInvoiceItem = (idx: number) => {
    if (this.invoice_items.$.length > 1) {
      this.invoice_items.$.splice(idx, 1);
      this.syncGlobalVatFromItems();
    } else {
      this.removeAllInvoiceItems();
    }
  };

  removeAllInvoiceItems = () => {
    this.invoice_items.$.splice(0, this.invoice_items.$.length);
    this.addEmptyInvoiceItem();
  };

  private createInvoiceItem = (item: InvoiceItemArgs): TInvoiceItemForm => {
    return new FormState({
      description: new FieldState(item.description).validators(required),
      quantity: new FieldState(
        item.quantity !== "" ? item.quantity : "1",
      ).validators(required, gt(0)),
      unit_price: new FieldState(item.unit_price).validators(required),
      accounting_account_id: new FieldState(item.accounting_account_id),
      amount_in_currency: new FieldState(item.amount_in_currency).validators(
        required,
        this.store.type === InvoiceTypeEnum.CreditNote
          ? lt(0, this.formatGtLtMessage)
          : () => null,
      ),
      cost_center_id: new FieldState(item.cost_center_id),
      tag_ids: new FieldState(item.tag_ids ?? []),
      vat_percent_informative: new FieldState(item.vat_percent_informative),
    });
  };

  autocompleteInvoiceItemsFromSplits = async () => {
    if (
      !this.our_work_budget_splits.$.size &&
      !this.expense_budget_splits.$.size &&
      !this.prepayment_splits.$.length
    ) {
      return;
    }

    this.invoice_items.$.splice(0, this.invoice_items.$.length);

    this.our_work_budget_splits.$.forEach((ourWorkSplit, id) => {
      const budgetItem = this.getOurWorkBudgetById(id);
      const budgetItemName = budgetItem?.title;
      if (!budgetItemName) return;
      if (!this.store.showBudgetItem(budgetItem)) return;
      const budgetItemVatPercentInformative =
        typeof budgetItem?.vat_percent_informative === "number"
          ? budgetItem.vat_percent_informative.toString()
          : null;

      const defaultAccountId =
        this.store.appStore.workspaceStore.getAccountingAccountForBudgetCategory(
          ourWorkSplit.$.budget_category_id.$,
          BudgetItemTypeEnum.OurWorkBudgetItem,
        )?.id;

      if (ourWorkSplit.$.invoice_from_positions.value) {
        ourWorkSplit.$.invoiced_positions.$.forEach((form, position) => {
          if (!form.$.isInvoiced.value) return;

          this.addInvoiceItem({
            description: `${budgetItemName} - ${position.title}`,
            amount_in_currency: form.$.amount.value,
            unit_price: form.$.unit_amount.value.value.toString(),
            accounting_account_id: defaultAccountId ?? "",
            quantity: form.$.quantity.$,
            cost_center_id: undefined,
            tag_ids: [],
            vat_percent_informative:
              budgetItemVatPercentInformative ??
              this.vat_percent_informative.$ ??
              "0",
          });
        });
      } else {
        this.addInvoiceItem({
          description: budgetItemName,
          amount_in_currency: ourWorkSplit.$.amount.value,
          unit_price: ourWorkSplit.$.amount.value.value.toString(),
          accounting_account_id: defaultAccountId ?? "",
          quantity: "",
          cost_center_id: undefined,
          tag_ids: [],
          vat_percent_informative:
            budgetItemVatPercentInformative ??
            this.vat_percent_informative.$ ??
            "0",
        });
      }
    });

    this.expense_budget_splits.$.forEach((expenseSplit, id) => {
      const budgetItem = this.getExpenseBudgetById(id);
      const name = budgetItem?.title;
      if (!name) return;

      const budgetItemVatPercentInformative =
        typeof budgetItem?.vat_percent_informative === "number"
          ? budgetItem.vat_percent_informative.toString()
          : null;

      const defaultAccountId =
        this.store.appStore.workspaceStore.getAccountingAccountForBudgetCategory(
          expenseSplit.$.budget_category_id.$,
          BudgetItemTypeEnum.ExpenseBudgetItem,
        )?.id;

      this.addInvoiceItem({
        description: name,
        amount_in_currency: expenseSplit.$.amount.value,
        unit_price: expenseSplit.$.amount.value.value.toString(),
        accounting_account_id: defaultAccountId ?? "",
        quantity: "",
        cost_center_id: undefined,
        tag_ids: [],
        vat_percent_informative:
          budgetItemVatPercentInformative ??
          this.vat_percent_informative.$ ??
          "0",
      });
    });

    this.prepayment_splits.$.forEach((split) => {
      const creditAccountLabel = compact(
        this.store.creditAccountsOptions.map(
          (creditAccount) =>
            creditAccount.options.find(
              (option) =>
                option.value === split.$.brand_credit_account_id.value,
            )?.label,
        ),
      )[0];
      const name = split.$.title.value
        ? split.$.title.value
        : creditAccountLabel
          ? creditAccountLabel
          : "";

      this.addInvoiceItem({
        description: name,
        amount_in_currency: split.$.amount.value,
        unit_price: split.$.amount.value.value.toString(),
        accounting_account_id: "",
        quantity: "",
        cost_center_id: undefined,
        tag_ids: [],
        vat_percent_informative: this.vat_percent_informative.$ ?? "0",
      });
    });

    if (!this.invoice_items.$.length) {
      this.addEmptyInvoiceItem();
      return;
    }

    if (this.store.convertedAmountRequired) {
      await this.convertInvoiceItems();
    }
  };

  async convertInvoiceItems(): Promise<void> {
    const [data, error] = await this.bulkConvertFetcher.fetch({
      currency_id: this.currency_id.value,
      exchange_rate: this.workspace_exchange_rate.$.denominatedValue,
      workspace_company_id: this.workspace_company_id.$ || undefined,
      items: this.invoice_items.$.map((item, index) => ({
        amount_in_currency: item.$.amount_in_currency.$.denominatedValue,
        unit_price: item.$.unit_price.$
          ? denominate(
              Number(item.$.unit_price.$),
              this.store.appStore.workspaceStore.settings?.currency
                .denomination ?? 2,
            )
          : undefined,
        identifier: index.toString(),
      })),
    });

    if (error) return;
    if (!data?.bulkConvertAmountFromWorkspaceCurrencyUsingUserExchangeRates)
      return;

    this.invoice_items.$.forEach((item, index) => {
      const convertedItem =
        data?.bulkConvertAmountFromWorkspaceCurrencyUsingUserExchangeRates[
          index
        ];
      if (!convertedItem) return;

      item.$.amount_in_currency.onChange(
        NumberModel.fromDenomValue(
          convertedItem.amount,
          this.store.appStore.workspaceStore.settings?.currency.denomination ??
            2,
        ),
      );

      if (!convertedItem.unit_price) return;
      item.$.unit_price.onChange(
        NumberModel.fromDenomValue(
          convertedItem.unit_price,
          this.store.appStore.workspaceStore.settings?.currency.denomination ??
            2,
        ).value.toString(),
      );
    });

    await this.store.convertAmountUsingAPI();
  }

  syncInvoiceItemsWithCurrency = () => {
    this.invoice_items.$.forEach((i) => {
      i.$.amount_in_currency.onChange(
        i.$.amount_in_currency.value.setDenominator(
          this.store.selectedCurrency.denomination,
        ),
      );
    });
  };

  addExpenseBudgetSplit = (
    id: TExpenseBudgetItem["id"],
    amount: NumberModel,
    budgetCategoryId: BudgetCategory["id"],
    amountInPercent: NumberModel = new NumberModel(0, 2),
  ): void => {
    this.expense_budget_splits.$.set(
      id,
      new FormState({
        amount: new FieldState(amount).validators(
          required,
          this.store.type === InvoiceTypeEnum.CreditNote ? lt(0) : gt(0),
        ),
        amount_in_percent: new FieldState(amountInPercent),
        budget_category_id: new FieldState(budgetCategoryId),
      }),
    );
  };

  getOurWorkBudgetById = (id: string) => {
    return this.store.ourWorkBudgetItems.find((item) => item.id === id);
  };

  getExpenseBudgetById = (id: string) => {
    return this.store.expenseBudgetItems.find((item) => item.id === id);
  };

  @computed get invoicePositionAmountRule() {
    return this.store.type === InvoiceTypeEnum.CreditNote ? lt(0) : gt(0);
  }

  @action.bound addOurWorkBudgetSplit = (
    id: TOurWorkBudgetItem["id"],
    amount: NumberModel,
    budget_positions: BudgetPosition[],
    budgetCategoryId: BudgetCategory["id"],
    amountInPercent: NumberModel,
  ): void => {
    const invoiced_positions: TInvoicedPositionsForm = new FormState(new Map());
    budget_positions.forEach((position) => {
      if (this.store.canSkipPosition(position)) return;

      const quantity = position.quantity ?? 1;
      const invoicedAmount = position.invoiced_amount ?? 0;
      const unitAmount =
        position.unit_amount && position.unit_amount > 0
          ? position.unit_amount
          : invoicedAmount > 0
            ? invoicedAmount / quantity
            : 0;

      const isInvoiced = (position.invoiced_amount ?? 0) > 0;
      invoiced_positions.$.set(
        position,
        new FormState({
          isInvoiced: new FieldState(isInvoiced),
          amount: new FieldState(
            new NumberModel(
              invoicedAmount,
              this.store.appStore.workspaceStore.settings?.currency
                .denomination!,
            ),
          ),
          quantity: new FieldState(quantity.toString()).validators(gt(0)),
          unit_amount: new FieldState(
            new NumberModel(
              unitAmount,
              this.store.appStore.workspaceStore.settings?.currency
                .denomination!,
            ),
          ),
        })
          .compose()
          .validators(($) => {
            if (!$.isInvoiced.value) return;
            return (
              this.invoicePositionAmountRule($.amount.$) ||
              this.invoicePositionAmountRule($.unit_amount.$)
            );
          }),
      );
    });

    const invoice_from_positions: boolean = budget_positions.some(
      (position) => (position.invoiced_amount ?? 0) > 0,
    );

    this.our_work_budget_splits.$.set(
      id,
      new FormState({
        amount: new FieldState(amount).validators(
          required,
          this.store.type === InvoiceTypeEnum.CreditNote ? lt(0) : gt(0),
        ),
        amount_in_percent: new FieldState(amountInPercent),
        invoice_from_positions: new FieldState(invoice_from_positions),
        budget_category_id: new FieldState(budgetCategoryId),
        invoiced_positions,
      }),
    );
  };

  addPrepaymentSplit = (
    brandCreditAccountId: string,
    brandId: string,
    amount: NumberModel,
    title?: string,
  ) => {
    this.prepayment_splits.$.push(
      this.createPrepaymentSplit(brandCreditAccountId, brandId, amount, title),
    );
  };

  removePrepaymentSplit = (idx: number) => {
    this.prepayment_splits.$.splice(idx, 1);
  };

  @action.bound
  handleExchangeRateChange = (newExchangeRate: string) => {
    NumberModel.setFieldState(this.exchange_rate, newExchangeRate);
  };

  handleChangeWorkspaceExchangeRate = (newExchangeRate: string) => {
    const oldExchangeRate =
      this.workspace_exchange_rate.value.value !== 0
        ? this.workspace_exchange_rate.value.value
        : 1;

    this.invoice_items.$.forEach(({ $ }) => {
      $.unit_price.onChange(
        (Number($.unit_price.value) / oldExchangeRate).toString(),
      );
      NumberModel.setFieldState(
        $.amount_in_currency,
        $.amount_in_currency.value.value / oldExchangeRate,
      );
    });

    const parsedExchangeRate =
      parseFloat(newExchangeRate) === 0 ? 1 : Number(newExchangeRate);

    NumberModel.setFieldState(this.workspace_exchange_rate, parsedExchangeRate);

    this.invoice_items.$.forEach(({ $ }) => {
      $.unit_price.onChange(
        NumberModel.fromValue(
          Number($.unit_price.value) * parsedExchangeRate,
          this.store.appStore.workspaceStore.settings?.currency.denomination ??
            2,
        ).value.toString(),
      );
      NumberModel.setFieldState(
        $.amount_in_currency,
        $.amount_in_currency.value.value * parsedExchangeRate,
      );
    });

    this.store.convertAmountUsingAPI({
      exchangeRate: denominate(
        Number(newExchangeRate),
        EXCHANGE_RATE_DENOMINATION,
      ),
    });
  };

  private createPrepaymentSplit = (
    brandCreditAccountId: string,
    brandId: string,
    amount: NumberModel,
    title?: string,
  ) => {
    const brand_credit_account_id = new FieldState(brandCreditAccountId);
    return new FormState({
      brand_credit_account_id,
      brand_id: new FieldState(brandId).validators((value) => {
        if (brand_credit_account_id.value === "new") return required(value);
        return null;
      }),
      title: new FieldState(title).validators((value) => {
        if (brand_credit_account_id.value === "new") return required(value);
        return null;
      }),
      amount: new FieldState(amount).validators(
        required,
        this.store.type === InvoiceTypeEnum.CreditNote ? lte(0) : gte(0),
        (value) => {
          if (!brand_credit_account_id.value) {
            return null;
          }

          const oiPrepaymentSplit = this.store.getOIPrepaymentSplitBy(
            brand_credit_account_id.value,
          );
          if (!oiPrepaymentSplit) return null;

          const maxAmount =
            oiPrepaymentSplit.amount - oiPrepaymentSplit.amount_left;
          if (Math.abs(value.denominatedValue) < Math.abs(maxAmount)) {
            return (
              t`Unable to edit prepayment amount less than` +
              ` ${nominate(maxAmount, this.store.appStore.workspaceStore.settings!.currency.denomination)}`
            );
          }

          return null;
        },
      ),
      amount_in_percent: new FieldState(new NumberModel(0, 2)),
    });
  };

  addOrder = (id?: string | string[]) => {
    if (!id) return;
    if (!Array.isArray(id)) id = [id];
    this.order_ids.onChange([
      ...this.order_ids.value,
      ...id.filter((i) => !this.order_ids.value.includes(i)),
    ]);
  };

  addMedia = (id?: string | string[]) => {
    if (!id) return;
    if (!Array.isArray(id)) id = [id];
    this.media_ids.onChange([
      ...this.media_ids.value,
      ...id.filter((i) => !this.media_ids.value.includes(i)),
    ]);
  };

  removeMedia = (id?: string | string[]) => {
    if (!id) return;
    if (!Array.isArray(id)) id = [id];
    this.media_ids.onChange(
      this.media_ids.value.filter((i) => !id.includes(i)),
    );
  };

  removeOrder = (id?: string | string[]) => {
    if (!id) return;
    if (!Array.isArray(id)) id = [id];
    this.order_ids.onChange(
      this.order_ids.value.filter((i) => !id.includes(i)),
    );
  };

  @action.bound
  handleChangeGlobalVat(val: string) {
    this.vat_percent_informative.onChange(val);
    this.invoice_items.$.forEach((item) => {
      item.$.vat_percent_informative.onChange(val);
    });
  }

  @action.bound
  syncGlobalVatFromItems() {
    if (this.invoice_items.$.length === 0) return;

    const selectedVats = this.invoice_items.$.map(
      (item) => item.$.vat_percent_informative.value,
    );
    const firstSelectedVat = selectedVats[0];
    const allSelectedVatsAreTheSame = selectedVats.every(
      (vat) => vat === firstSelectedVat,
    );

    if (allSelectedVatsAreTheSame) {
      if (this.vat_percent_informative.value === firstSelectedVat) return;
      this.vat_percent_informative.onChange(firstSelectedVat ?? "");
    } else {
      if (this.vat_percent_informative.value === "") return;
      this.vat_percent_informative.onChange("");
    }
  }

  @action.bound
  handleChangeExchangeRateSource(newExchangeRateSource: ExchangeRateSource) {
    this.exchange_rate_source.onChange(newExchangeRateSource);

    if (newExchangeRateSource === ExchangeRateSource.Custom) {
      this.resetExchangeRate();
    }

    this.store.getExchangeRate();
  }

  @action.bound
  handleChangeWorkspaceExchangeRateSource(
    newWorkspaceExchangeRateSource: ExchangeRateSource,
  ) {
    this.workspace_exchange_rate_source.onChange(
      newWorkspaceExchangeRateSource,
    );

    if (newWorkspaceExchangeRateSource === ExchangeRateSource.Custom) {
      this.handleChangeWorkspaceExchangeRate("1");
    }

    this.store.getWorkspaceExchangeRate();
  }

  get data() {
    return {
      team_ids: this.team_ids.$,
      client_id: this.client_id.$,
      brand_ids: this.brand_ids.$,
      billing_entity_id: this.billing_entity_id.$,
      invoice_no: this.store.invoice_no ? this.invoice_no.$ : undefined,
      invoice_order_sequence_id: this.invoice_order_sequence_id.$,
      contact_id: this.contact_id.$,
      project_ids: this.project_ids.$,
      assignable_file_ids: this.media_ids.$,
      currency_id: this.currency_id.$,
      order_ids: this.order_ids.value,
      outgoing_proforma_invoice_ids: this.outgoing_proforma_invoice_ids.value,
      proof_of_payments_ids: this.payment_receipt_ids.value,
      note: this.note.value,
      internal_note: this.internal_note.value,
      text_on_invoice: this.text_on_invoice.value,
      owner_id: this.owner_id.$,
      vat_percent_informative:
        this.vat_percent_informative.$.length > 0
          ? Number(this.vat_percent_informative.$)
          : null,
      vat_reverse_charge: this.vat_reverse_charge.$,
      refresh_billing_entity: this.refresh_billing_entity.$,
      due_date: this.due_date.value && toApiDate(this.due_date.value),
      issue_date: this.issue_date.value && toApiDate(this.issue_date.value),
      date_of_supply:
        this.date_of_supply.value && toApiDate(this.date_of_supply.value),
      total_in_words: this.total_in_words.$,
      workspace_company_id: this.workspace_company_id.$,
      workspace_bank_account_id: this.workspace_bank_account_id.$ ?? "",

      invoice_items: this.invoice_items.$.map((item) => ({
        description: item.$.description.$,
        amount_in_currency: item.$.amount_in_currency.$.denominatedValue,
        unit_price: denominate(
          Number(item.$.unit_price.$),
          this.store.appStore.workspaceStore.settings?.currency.denomination ??
            2,
        ),
        quantity: Number(item.$.quantity.$),
        accounting_account_id: item.$.accounting_account_id.$,
        accounting_centre_id: item.$.cost_center_id.$ ?? null,
        tag_ids: item.$.tag_ids.$ ?? [],
        vat_percent_informative: Number(item.$.vat_percent_informative.$),
      })),

      our_work_budget_splits: this.store.isTimeAndMaterialsSelected
        ? this.store.timeAndMaterialsStore.workTableState.getBudgetSplits(
            "our_work",
          )
        : Array.from(this.our_work_budget_splits.$).map(([id, item]) => ({
            our_work_budget_item_id: id,
            amount: item.$.amount.$.denominatedValue,
            amount_in_percent: item.$.amount_in_percent.$.denominatedValue,
            budget_positions: item.$.invoice_from_positions.value
              ? Array.from(item.$.invoiced_positions.$)
                  .filter(([_, form]) => form.$.isInvoiced.value)
                  .map(([position, form]) => ({
                    our_work_budget_item_id: id,
                    time_tracking_work_type_id:
                      position.time_tracking_work_type_id,
                    amount: form.$.amount.$.denominatedValue,
                    unit_amount: form.$.unit_amount.$.denominatedValue,
                    quantity:
                      form.$.quantity.$ === "" || form.$.quantity.$ === "0"
                        ? 1
                        : Number(form.$.quantity.$),
                  }))
              : undefined,
          })),

      expense_budget_splits: this.store.isTimeAndMaterialsSelected
        ? this.store.timeAndMaterialsStore.expenseTableState.getBudgetSplits(
            "expense",
          )
        : Array.from(this.expense_budget_splits.$).map(([id, item]) => ({
            expense_budget_item_id: id,
            amount: item.$.amount.$.denominatedValue,
          })),

      prepayment_splits: this.prepayment_splits.$.map((item) => ({
        brand_credit_account_id:
          item.$.brand_credit_account_id.$ === "new"
            ? ""
            : item.$.brand_credit_account_id.$,
        brand_id: item.$.brand_id.$ || undefined,
        amount: item.$.amount.$.denominatedValue,
        title: item.$.title.$ || undefined,
      })),

      exchange_rate: this.store.showInvoiceExchangeRate
        ? this.exchange_rate.$.denominatedValue
        : denominate(1, EXCHANGE_RATE_DENOMINATION),
      exchange_rate_source: this.store.showInvoiceExchangeRate
        ? this.exchange_rate_source.$
        : ExchangeRateSource.Custom,

      workspace_exchange_rate: this.store.showWorkspaceExchangeRate
        ? this.workspace_exchange_rate.$.denominatedValue
        : denominate(1, EXCHANGE_RATE_DENOMINATION),
      workspace_exchange_rate_source: this.store.showWorkspaceExchangeRate
        ? this.workspace_exchange_rate_source.$
        : ExchangeRateSource.Custom,

      credit_note_outgoing_invoice_id:
        this.store.type === InvoiceTypeEnum.CreditNote
          ? this.credit_note_outgoing_invoice_id.$
          : undefined,
      invoice_payment_method_id: this.invoicePaymentMethodId.$,
    };
  }
}
